import * as fs from "node:fs";
import * as path from "node:path";
import { spawnSync } from "node:child_process";

import {
  CliError,
  EXIT_INPUT,
  EXIT_OK,
  sha256Text,
  scriptJsonPath,
  type CommandOutput,
} from "../../common.js";
import { emptyContext } from "../../domain/ingest/video-context.js";
import { buildVideoStateEvidencePlan } from "../../domain/ingest/video-state-evidence.js";
import {
  validateContinuousEpisodeSequence,
  type VideoSourceEpisodeIdentity,
} from "../../domain/ingest/video-source-evolution.js";
import { videoSourceFingerprint } from "../../infra/ingest/video-source-snapshot.js";
import { requireScriptV4 } from "../../domain/script/v4/parse.js";
import { discoverEpisodes } from "../../infra/ingest/video-discovery.js";
import {
  materializeVideoStateEvidence,
  type VideoFrameExtractor,
} from "../../infra/ingest/video-state-evidence.js";
import { resolveSource } from "../../infra/ingest/source-resolver.js";
import { withIngestWorkspaceLock } from "../../infra/ingest/workspace-lock.js";
import { WorkspaceStore } from "../../infra/ingest/workspace-store.js";

type Dict = Record<string, unknown>;

export interface GenerateVideoStateEvidenceInput {
  scriptPath: string;
  sourcePath: string;
  workspacePath: string;
  force?: boolean;
  signal?: AbortSignal;
  extractor?: VideoFrameExtractor;
}

export interface GenerateVideoStateEvidenceResult {
  output: string;
  states: number;
  identities: number;
  samples: number;
  ready: number;
  unavailable: number;
  sourceSha256: string;
}

function inputError(message: string, code: string, hint: string, actual: string[]): CliError {
  return new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: code,
    hint,
    data: { actual },
  });
}

/**
 * Build cheap source identities from NAS metadata. This command only needs to
 * invalidate a derived frame cache; it must not reread every video byte merely
 * to calculate a cryptographic content digest before ffmpeg can seek into it.
 */
function sourceIdentities(sourcePath: string): VideoSourceEpisodeIdentity[] {
  const episodes = discoverEpisodes(resolveSource(sourcePath));
  const sequenceError = validateContinuousEpisodeSequence(episodes);
  if (sequenceError) {
    throw inputError(
      "Video episode files are not a continuous 1..N sequence.",
      "EVIDENCE_VIDEO_SEQUENCE_INVALID",
      "Rename the linked videos so every script episode has one matching numbered file.",
      [sequenceError],
    );
  }
  return episodes.map((episode) => {
    const absolutePath = path.resolve(episode.path);
    const stat = fs.statSync(absolutePath);
    const sizeBytes = stat.size;
    const mtimeMs = Math.trunc(stat.mtimeMs);
    const probe = spawnSync("ffprobe", [
      "-v", "error",
      "-show_entries", "format=duration",
      "-of", "default=noprint_wrappers=1:nokey=1",
      absolutePath,
    ], { encoding: "utf-8" });
    const durationSeconds = Number(probe.stdout.trim());
    const durationMs = probe.status === 0 && Number.isFinite(durationSeconds) && durationSeconds > 0
      ? Math.round(durationSeconds * 1_000)
      : undefined;
    return {
      episode: episode.episode,
      path: absolutePath,
      ext: episode.ext,
      mime: episode.mime,
      // The field name is retained for the existing evidence contract. For
      // this post-process its value intentionally fingerprints stable metadata.
      sha256: sha256Text(JSON.stringify({ absolutePath, sizeBytes, mtimeMs })),
      size_bytes: sizeBytes,
      mtime_ms: mtimeMs,
      duration_ms: durationMs,
    };
  });
}

export async function generateVideoStateEvidence(
  input: GenerateVideoStateEvidenceInput,
): Promise<GenerateVideoStateEvidenceResult> {
  const scriptPath = path.resolve(input.scriptPath);
  const sourcePath = path.resolve(input.sourcePath);
  const workspacePath = path.resolve(input.workspacePath);
  const script = requireScriptV4(JSON.parse(fs.readFileSync(scriptPath, "utf-8")));
  const sources = sourceIdentities(sourcePath);
  if (sources.length !== script.episodes.length) {
    throw inputError(
      "The linked video count does not match the script episode count.",
      "EVIDENCE_VIDEO_EPISODE_COUNT_MISMATCH",
      "Bind the complete source-video folder for this script, then retry.",
      [`script=${script.episodes.length}`, `videos=${sources.length}`],
    );
  }

  const sourceSha256 = videoSourceFingerprint(sources);
  // The standalone path deliberately consumes only the final Script v4. It
  // never consults Gemini ingest documents or makes a model request.
  const plan = buildVideoStateEvidencePlan([], script, emptyContext(), sources, sourceSha256);
  const manifest = await withIngestWorkspaceLock(workspacePath, async () => {
    const store = new WorkspaceStore(workspacePath);
    store.ensure();
    return materializeVideoStateEvidence(
      store,
      plan,
      input.signal,
      input.extractor,
      { reuseExistingFrames: input.force !== true },
    );
  });
  const samples = [
    ...manifest.items.flatMap((item) => item.samples),
    ...(manifest.identities ?? []).flatMap((item) => item.samples),
  ];
  return {
    output: path.join(workspacePath, "evidence", "state-evidence.json"),
    states: manifest.items.length,
    identities: manifest.identities?.length ?? 0,
    samples: samples.length,
    ready: samples.filter((sample) => sample.frame_status === "ready").length,
    unavailable: samples.filter((sample) => sample.frame_status === "unavailable").length,
    sourceSha256,
  };
}

export async function commandGenerateVideoStateEvidence(opts: Dict): Promise<[CommandOutput, number]> {
  const scriptPath = scriptJsonPath(opts);
  const sourcePath = typeof opts["source_path"] === "string" ? opts["source_path"].trim() : "";
  const workspacePath = typeof opts["workspace_path"] === "string" ? opts["workspace_path"].trim() : "";
  if (!sourcePath) {
    throw inputError("--source-path is required.", "EVIDENCE_SOURCE_REQUIRED", "Pass the linked source-video file or folder.", ["--source-path <path>"]);
  }
  if (!workspacePath) {
    throw inputError("--workspace-path is required.", "EVIDENCE_WORKSPACE_REQUIRED", "Pass the project workspace directory.", ["--workspace-path <path>"]);
  }
  const result = await generateVideoStateEvidence({
    scriptPath,
    sourcePath,
    workspacePath,
    force: opts["force"] === true,
  });
  return [{
    text: `Generated ${result.ready}/${result.samples} original-video reference frames for ${result.states} actor states.`,
    data: {
      output: result.output,
      states: result.states,
      identities: result.identities,
      samples: result.samples,
      ready: result.ready,
      unavailable: result.unavailable,
      source_sha256: result.sourceSha256,
    },
  }, EXIT_OK];
}
